import { getRegisteredGlobal, registerGlobal } from "@trumpf/ux.utils";
import { IconMetaData, IconProps, IconSetType, IconThemeType } from "./types";
import { AVAILABLE_ICONS, NS_ICON_REGISTRY } from "./constants";
import { fixAssetsPath } from "./utils";
import { debounce } from "../../utils/debounce";

export interface IconRegistryType {
    readonly assetsPath: string;
    readonly theme: IconThemeType;
    getRegisteredIcons: <T extends IconMetaData>() => T[];
    resolveAssetUrl: (iconProps: IconProps) => string | null;
    registerIcon: (iconData: IconMetaData) => IconRegistryType;
    registerIconSet: (iconSet: IconSetType) => IconRegistryType;
    setAssetsPath: (path: string) => IconRegistryType;
    setTheme: (value: IconThemeType) => IconRegistryType;
}

class IconRegistry implements IconRegistryType {
    private assetsPathInternal: string | undefined;
    private assetsPathExplicit: string | undefined;
    private themeExplicit: IconThemeType;
    private themeDetected: IconThemeType;
    private readonly customIcons: IconMetaData[] = [];
    private readonly iconSets: Record<string, IconSetType> = {};

    constructor() {
        this.themeDetected = this.detectTheme();
        document.addEventListener("uxThemeChange", this.handleThemeChange);
    }

    private handleThemeChange = debounce(() => {
        const oldValue = this.assetsPath;
        this.assetsPathInternal = this.assetsPathExplicit;
        this.themeDetected = this.detectTheme();
        if (this.assetsPath !== oldValue) {
            this.updateIcons();
        }
    }, 0);

    public get theme(): IconThemeType {
        if (this.themeExplicit !== undefined) {
            return this.themeExplicit;
        }
        if (this.themeDetected === undefined) {
            this.themeDetected = this.detectTheme();
        }
        return this.themeDetected;
    }

    public setTheme(value: IconThemeType): IconRegistryType {
        this.themeExplicit = value;
        return this;
    }

    public get assetsPath(): string {
        if (this.assetsPathInternal === undefined) {
            this.assetsPathInternal = this.detectAssetsPath();
        }
        return fixAssetsPath(this.assetsPathInternal);
    }

    public setAssetsPath(value: string): IconRegistryType {
        const oldValue = this.assetsPath;
        this.assetsPathExplicit = fixAssetsPath(value);
        this.assetsPathInternal = this.assetsPathExplicit;
        if (this.assetsPath !== oldValue) {
            this.updateIcons();
        }
        return this;
    }

    public registerIcon(iconData: IconMetaData): IconRegistryType {
        const item = this.getRegisteredIcons().find((el) => el.name === iconData.name) || null;
        if (item !== null) {
            // eslint-disable-next-line no-console
            console.warn(`Warning: Icon ${iconData.name} is already registered`);
        } else {
            this.customIcons.push(iconData);
        }
        return this;
    }

    public registerIconSet(iconSet: IconSetType): IconRegistryType {
        if (this.iconSets[iconSet.ns] !== undefined) {
            // eslint-disable-next-line no-console
            console.warn(`Warning: IconSet ${iconSet.ns} is already registered`);
        } else {
            this.iconSets[iconSet.ns] = iconSet;
            this.updateIcons();
        }
        return this;
    }

    public getRegisteredIcons<T extends IconMetaData>(): T[] {
        return [...AVAILABLE_ICONS[this.themeDetected], ...this.customIcons] as T[];
    }

    public resolveAssetUrl(iconProps: IconProps): string | null {
        const { name } = iconProps;
        if (name.includes(":")) {
            return this.getIconSetAssetUrl(iconProps);
        }
        const fileName = this.getIconBaseName(iconProps);
        if (fileName === null) {
            return null;
        }
        return `${fixAssetsPath(this.assetsPath)}${fileName}.svg`;
    }

    private getIconBaseName = (iconProps: IconProps): string | null => {
        let fileName: string | null = null;
        const registeredIcons = this.getRegisteredIcons();
        let iconMetaData = registeredIcons.find((icon) => icon.name === iconProps.name);
        if (iconMetaData !== undefined) {
            const isFilled =
                // @ts-expect-error Check for typing
                (iconProps.filled || iconMetaData.outlineWeights.length === 0) && iconMetaData.filledWeights.length > 0;
            const weights = isFilled ? iconMetaData.filledWeights : iconMetaData.outlineWeights;
            fileName = this.getIconVariantName({
                name: iconProps.name,
                filled: isFilled,
                // @ts-expect-error Check for typing
                weight: this.getIconWeight(iconProps.weight, weights)
            });
        } else {
            iconMetaData = registeredIcons.find((icon) => icon.legacyNames?.[iconProps.name] !== undefined);
            if (iconMetaData === undefined) {
                return fileName;
            }
            // @ts-expect-error Check for typing
            const isFilled = iconMetaData.legacyNames[iconProps.name].filled;
            const weights = isFilled ? iconMetaData.filledWeights : iconMetaData.outlineWeights;
            fileName = this.getIconVariantName({
                name: iconMetaData.name,
                filled: isFilled,
                // @ts-expect-error Check for typing
                weight: this.getIconWeight(iconMetaData.legacyNames[iconProps.name].weight, weights)
            });
        }
        return fileName || null;
    };

    private getIconSetAssetUrl = (iconProps: IconProps): string | null => {
        const parts = iconProps.name.split(":");
        if (parts.length !== 2) {
            return null;
        }
        const iconSet = this.iconSets[parts[0]];
        if (iconSet === undefined) {
            return null;
        }
        const baseName = this.getIconVariantName({
            ...iconProps,
            name: parts[1]
        });
        return `${fixAssetsPath(iconSet.assetsPath)}${baseName}.svg`;
    };

    private getIconVariantName = ({ name, filled, weight }: IconProps): string => {
        const parts = [name];
        if (filled) {
            parts.push(`filled`);
        }
        if (typeof weight === "string" && weight !== "") {
            parts.push(weight);
        }
        return parts.join(`--`);
    };

    // @ts-expect-error Check for typing
    private getIconWeight = (weight: string, availableWeights: string[]): string => {
        if (!Array.isArray(availableWeights)) {
            // @ts-expect-error Check for typing
            return;
        }
        if (availableWeights.includes(weight)) {
            return weight;
        } else if (availableWeights.includes("normal")) {
            return "normal";
        } else if (availableWeights.includes("light")) {
            return "light";
        } else if (availableWeights.includes("bold")) {
            return "bold";
        }
    };

    private detectTheme = (): "web" | "responsive" => {
        return document.getElementsByClassName("ux-theme-responsive").length > 0 ? "responsive" : "web";
    };

    private detectAssetsPath = (): string => {
        const detectorEl = document.createElement("div");
        detectorEl.className = "ux-icon__svg-path-detector";
        document.body.appendChild(detectorEl);
        let svgPath: string;
        try {
            svgPath = getComputedStyle(detectorEl)
                .backgroundImage.replace(`url("`, "")
                .replace(/[a-zA-Z0-9_.]*"\)$/, "");
        } catch {
            svgPath = `/themes/${this.theme}/assets/icons/svg/`;
        }
        detectorEl.remove();
        return fixAssetsPath(svgPath);
    };

    private updateIcons = debounce(() => {
        document.querySelectorAll<HTMLUxIconElement>(`ux-icon.hydrated`).forEach((el) => {
            el.reload();
        });
        document.querySelectorAll<HTMLElement>(`ux-button, ux-tag`).forEach((el) => {
            el.shadowRoot?.querySelector<HTMLUxIconElement>(`ux-icon.hydrated`)?.reload();
        });
    }, 10);
}

export const getIconRegistry = (): IconRegistryType => {
    let instance = getRegisteredGlobal<IconRegistryType>(NS_ICON_REGISTRY);
    if (typeof instance === "undefined") {
        instance = new IconRegistry();
        registerGlobal(instance, NS_ICON_REGISTRY);
    }
    return instance;
};
